import apiClient from "./client";
import {
  AnalyticsData,
  OrdersSummaryMetrics,
  IMenuItem,
} from "shared";

// Enhanced types for analytics
export interface SalesTrendData {
  date: string;
  revenue: number;
  profit: number;
  orders: number;
}

export interface ProductProfitabilityData {
  menuItem: IMenuItem;
  revenue: number;
  cost: number;
  profit: number;
  profitMargin: number;
  quantitySold: number;
}

export interface BestSellerData {
  menuItem: IMenuItem;
  quantitySold: number;
  revenue: number;
  averageOrderValue: number;
}

export interface SlowMoverData {
  menuItem: IMenuItem;
  quantitySold: number;
  daysInStock: number;
  lastSold: string | null;
  inventoryValue: number;
}

export interface InventoryReportData {
  totalValue: number;
  totalProducts: number;
  lowStockItems: number;
  categories: Array<{
    category: string;
    value: number;
    itemCount: number;
  }>;
}

export interface ManufacturingCostHistory {
  date: string;
  costPerUnit: number;
  batch: string;
}

// Analytics API
export const analyticsApi = {
  // Basic analytics
  async getAnalytics(startDate?: Date, endDate?: Date): Promise<AnalyticsData> {
    const params = new URLSearchParams();
    if (startDate) params.append('start', startDate.toISOString());
    if (endDate) params.append('end', endDate.toISOString());

    const { data } = await apiClient.get<AnalyticsData>(`/analytics?${params.toString()}`);
    return data;
  },

  async getTodayAnalytics(): Promise<AnalyticsData> {
    const { data } = await apiClient.get<AnalyticsData>('/analytics/today');
    return data;
  },

  async getThisMonthAnalytics(): Promise<AnalyticsData> {
    const { data } = await apiClient.get<AnalyticsData>('/analytics/month');
    return data;
  },

  async getPeriodAnalytics(period: 'week' | 'month' | 'year'): Promise<AnalyticsData> {
    const { data } = await apiClient.get<AnalyticsData>(`/analytics/period?period=${period}`);
    return data;
  },

  // Advanced analytics
  async getProfitByDateRange(startDate: Date, endDate: Date): Promise<{
    date: string;
    profit: number;
    revenue: number;
    cost: number;
  }[]> {
    const params = new URLSearchParams({
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    });

    const { data } = await apiClient.get(`/analytics/profit/date-range?${params.toString()}`);
    return data;
  },

  async getProductProfitability(startDate?: Date, endDate?: Date): Promise<ProductProfitabilityData[]> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate.toISOString());
    if (endDate) params.append('endDate', endDate.toISOString());

    const { data } = await apiClient.get<ProductProfitabilityData[]>(`/analytics/products/profitability?${params.toString()}`);
    return data;
  },

  async getManufacturingCostHistory(productId: string): Promise<ManufacturingCostHistory[]> {
    const { data } = await apiClient.get<ManufacturingCostHistory[]>(`/analytics/manufacturing/cost-history/${productId}`);
    return data;
  },

  async getInventoryReport(): Promise<InventoryReportData> {
    const { data } = await apiClient.get<InventoryReportData>('/analytics/inventory/report');
    return data;
  },

  async getSlowMovingProducts(startDate?: Date, endDate?: Date): Promise<SlowMoverData[]> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate.toISOString());
    if (endDate) params.append('endDate', endDate.toISOString());

    const { data } = await apiClient.get<SlowMoverData[]>(`/analytics/products/slow-movers?${params.toString()}`);
    return data;
  },

  async getBestSellers(startDate?: Date, endDate?: Date): Promise<BestSellerData[]> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate.toISOString());
    if (endDate) params.append('endDate', endDate.toISOString());

    const { data } = await apiClient.get<BestSellerData[]>(`/analytics/products/best-sellers?${params.toString()}`);
    return data;
  },

  async getSalesTrend(startDate?: Date, endDate?: Date): Promise<SalesTrendData[]> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate.toISOString());
    if (endDate) params.append('endDate', endDate.toISOString());

    const { data } = await apiClient.get<SalesTrendData[]>(`/analytics/sales/trend?${params.toString()}`);
    return data;
  },

  async getOrdersSummary(options?: {
    tab?: string;
    date?: string;
    customer?: string;
  }): Promise<OrdersSummaryMetrics> {
    const params = new URLSearchParams();
    if (options?.tab) params.append('tab', options.tab);
    if (options?.date) params.append('date', options.date);
    if (options?.customer) params.append('customer', options.customer);

    const { data } = await apiClient.get<OrdersSummaryMetrics>(`/analytics/orders/summary?${params.toString()}`);
    return data;
  },
};

// Utility functions
export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
};

export const formatPercent = (value: number): string => {
  return `${(value * 100).toFixed(1)}%`;
};

export const getProfitMarginColor = (margin: number): string => {
  if (margin >= 0.7) return '#10b981'; // green
  if (margin >= 0.5) return '#f59e0b'; // amber
  if (margin >= 0.3) return '#f97316'; // orange
  return '#ef4444'; // red
};

export const formatDate = (date: string | Date): string => {
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

export const formatDateTime = (date: string | Date): string => {
  return new Date(date).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};